import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const Ventures = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const cardWidth = window.innerWidth * 0.66 + 16; // 66vw card + gap-4
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.min(Math.max(newIndex, 0), 3));
    }
  };

  const scrollToIndex = (index) => {
    if (scrollRef.current) {
      const cardWidth = window.innerWidth * 0.66 + 16;
      scrollRef.current.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
    }
  };

  const cardClass = `group bg-white/70 backdrop-blur-md rounded-2xl p-6 shadow-lg border border-slate-200/50 h-full flex flex-col transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  return (
    <section id="ventures" ref={sectionRef} className="py-12 sm:py-16 md:py-24 bg-slate-50 relative overflow-hidden" data-testid="ventures-section">
      {/* Background Orbs */}
      <div className="absolute top-20 left-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-purple-500/18 to-blue-500/18 rounded-full blur-3xl animate-float-orb-2"></div>
      <div className="absolute top-1/3 right-1/4 w-56 h-56 sm:w-80 sm:h-80 bg-gradient-to-br from-blue-400/15 to-slate-400/15 rounded-full blur-3xl animate-float-orb-3"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6">Our Ventures</h2>
          <p className="text-base sm:text-xl text-slate-600 max-w-3xl mx-auto">
            Four distinct products unified by a common philosophy: use technology to solve real problems.
          </p>
        </div>

        {/* Mobile Carousel — tall portrait cards */}
        <div className="lg:hidden">
          <div 
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {/* Card 1 - Collabity */}
            <div className="w-[66vw] max-w-[66vw] snap-center flex-shrink-0">
              <div className={`rounded-3xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ height: 380 }}>
                <div className="relative h-[62%] overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600">
                  <img src="/venture-collabity.jpg" alt="Collabity" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-60" />
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2.5 py-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-semibold rounded-full">Platform</span>
                  </div>
                </div>
                <div className="p-4 bg-white h-[38%] flex flex-col justify-between min-w-0">
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-900 truncate">Collabity</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-snug break-words line-clamp-2">Where communities build together</p>
                  </div>
                  <Link to="/collabity" className="text-xs text-slate-900 font-semibold flex items-center mt-2" data-testid="venture-card-collabity-link">
                    Learn more <ArrowRight className="ml-1 w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Card 2 - Auto-Mate */}
            <div className="w-[66vw] max-w-[66vw] snap-center flex-shrink-0">
              <div className={`rounded-3xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ height: 380, transitionDelay: '100ms' }}>
                <div className="relative h-[62%] overflow-hidden bg-gradient-to-br from-purple-600 to-violet-700">
                  <img src="/venture-automate.jpg" alt="Auto-Mate" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-60" />
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2.5 py-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-semibold rounded-full">Consulting</span>
                  </div>
                </div>
                <div className="p-4 bg-white h-[38%] flex flex-col justify-between min-w-0">
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-900 truncate">Auto-Mate</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-snug break-words line-clamp-2">Smarter automation decisions</p>
                  </div>
                  <Link to="/automate" className="text-xs text-slate-900 font-semibold flex items-center mt-2" data-testid="venture-card-automate-link">
                    Learn more <ArrowRight className="ml-1 w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Card 3 - Eco-Escape */}
            <div className="w-[66vw] max-w-[66vw] snap-center flex-shrink-0">
              <div className={`rounded-3xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ height: 380, transitionDelay: '200ms' }}>
                <div className="relative h-[62%] overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600">
                  <img src="/venture-ecoescape.jpg" alt="Eco-Escape" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-60" />
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2.5 py-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-semibold rounded-full">Platform</span>
                  </div>
                </div>
                <div className="p-4 bg-white h-[38%] flex flex-col justify-between min-w-0">
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-900 truncate">Eco-Escape</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-snug break-words line-clamp-2">Travel with clarity</p>
                  </div>
                  <a href="https://ecoescape-oo4ud49.public.builtwithrocket.new/" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-900 font-semibold flex items-center mt-2" data-testid="venture-card-ecoescape-link">
                    Learn more <ArrowRight className="ml-1 w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>

            {/* Card 4 - JADE */}
            <div className="w-[66vw] max-w-[66vw] snap-center flex-shrink-0">
              <div className={`rounded-3xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ height: 380, transitionDelay: '300ms' }}>
                <div className="relative h-[62%] overflow-hidden bg-gradient-to-br from-slate-800 to-slate-950">
                  <img src="/venture-jade.jpg" alt="JADE" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-50" />
                  <div className="absolute bottom-3 left-3">
                    <span className="px-2.5 py-1 bg-white/20 backdrop-blur-md text-white text-[10px] font-semibold rounded-full">Brand</span>
                  </div>
                </div>
                <div className="p-4 bg-white h-[38%] flex flex-col justify-between min-w-0">
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-slate-900 truncate">JADE</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-snug break-words line-clamp-2">Clothes that speak</p>
                  </div>
                  <Link to="/jade" className="text-xs text-slate-900 font-semibold flex items-center mt-2" data-testid="venture-card-jade-link">
                    Learn more <ArrowRight className="ml-1 w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center items-center gap-2 mt-4">
            <button onClick={() => scrollToIndex(0)} className={`rounded-full transition-all ${activeIndex === 0 ? 'w-6 h-1.5 bg-slate-900' : 'w-1.5 h-1.5 bg-slate-300'}`} />
            <button onClick={() => scrollToIndex(1)} className={`rounded-full transition-all ${activeIndex === 1 ? 'w-6 h-1.5 bg-slate-900' : 'w-1.5 h-1.5 bg-slate-300'}`} />
            <button onClick={() => scrollToIndex(2)} className={`rounded-full transition-all ${activeIndex === 2 ? 'w-6 h-1.5 bg-slate-900' : 'w-1.5 h-1.5 bg-slate-300'}`} />
            <button onClick={() => scrollToIndex(3)} className={`rounded-full transition-all ${activeIndex === 3 ? 'w-6 h-1.5 bg-slate-900' : 'w-1.5 h-1.5 bg-slate-300'}`} />
          </div>
        </div>

        {/* Desktop Grid — cards with image headers */}
        <div className="hidden lg:grid grid-cols-2 gap-8">
          {/* Collabity */}
          <div className={`group rounded-2xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative h-44 overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600">
              <img src="/venture-collabity.jpg" alt="Collabity" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-50" />
              <div className="absolute bottom-3 left-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-md text-white text-xs font-semibold rounded-full">Platform</span>
              </div>
            </div>
            <div className="p-6 bg-white/70 backdrop-blur-md">
              <h3 className="text-2xl font-bold text-slate-900 mb-1">Collabity</h3>
              <p className="text-base font-semibold text-slate-700 mb-3">Where communities build together</p>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">A next-generation project management platform that combines community-driven collaboration with micro-investment capabilities.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Project Management</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Micro-Investment</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Community Governance</span>
              </div>
              <Link to="/collabity" className="text-sm text-slate-900 font-semibold flex items-center group-hover:translate-x-1 transition-transform" data-testid="venture-card-collabity-link-desktop">
                Learn more <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Auto-Mate */}
          <div className={`group rounded-2xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '150ms' }}>
            <div className="relative h-44 overflow-hidden bg-gradient-to-br from-purple-600 to-violet-700">
              <img src="/venture-automate.jpg" alt="Auto-Mate" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-50" />
              <div className="absolute bottom-3 left-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-md text-white text-xs font-semibold rounded-full">Consulting</span>
              </div>
            </div>
            <div className="p-6 bg-white/70 backdrop-blur-md">
              <h3 className="text-2xl font-bold text-slate-900 mb-1">Auto-Mate</h3>
              <p className="text-base font-semibold text-slate-700 mb-3">Intelligence for automation decisions</p>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">An automation consulting practice that helps organisations understand what to automate and what not to. We audit processes and guide implementation with ROI focus.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Process Audit</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Automation Scoring</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">ROI Analysis</span>
              </div>
              <Link to="/automate" className="text-sm text-slate-900 font-semibold flex items-center group-hover:translate-x-1 transition-transform">
                Learn more <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Eco-Escape */}
          <div className={`group rounded-2xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '300ms' }}>
            <div className="relative h-44 overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600">
              <img src="/venture-ecoescape.jpg" alt="Eco-Escape" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-50" />
              <div className="absolute bottom-3 left-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-md text-white text-xs font-semibold rounded-full">Platform</span>
              </div>
            </div>
            <div className="p-6 bg-white/70 backdrop-blur-md">
              <h3 className="text-2xl font-bold text-slate-900 mb-1">Eco-Escape</h3>
              <p className="text-base font-semibold text-slate-700 mb-3">Travel with clarity</p>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">A UK-focused sustainable travel platform that calculates carbon footprints, identifies low-impact alternatives, and helps travellers build eco-friendly holidays.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Carbon Calculation</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Route Optimisation</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Sustainable Stays</span>
              </div>
              <a href="https://ecoescape-oo4ud49.public.builtwithrocket.new/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-900 font-semibold flex items-center group-hover:translate-x-1 transition-transform">
                Learn more <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>
          </div>

          {/* JADE */}
          <div className={`group rounded-2xl overflow-hidden shadow-lg border border-slate-200/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '450ms' }}>
            <div className="relative h-44 overflow-hidden bg-gradient-to-br from-slate-800 to-slate-950">
              <img src="/venture-jade.jpg" alt="JADE" loading="lazy" className="w-full h-full object-cover mix-blend-overlay opacity-50" />
              <div className="absolute bottom-3 left-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-md text-white text-xs font-semibold rounded-full">Brand</span>
              </div>
            </div>
            <div className="p-6 bg-white/70 backdrop-blur-md">
              <h3 className="text-2xl font-bold text-slate-900 mb-1">JADE</h3>
              <p className="text-base font-semibold text-slate-700 mb-3">Clothes that speak</p>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">A premium, community-led smart clothing brand where each piece carries meaning. JADE apparel features scannable tags, limited collections, and a community of collectors.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Limited Collections</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Scannable Identity</span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Community-Led</span>
              </div>
              <Link to="/jade" className="text-sm text-slate-900 font-semibold flex items-center group-hover:translate-x-1 transition-transform">
                Learn more <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Ventures;
